import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import styled, { keyframes, css } from 'styled-components'
import { usePublicFeaturedProducts } from '@src/hooks/queries/useFeaturedProducts'
import { usePublicSettings } from '@src/hooks/queries/useSettings'
import { useCategoryImageMap } from '@src/hooks/useCategoryImageMap'
import type { FeaturedProduct } from '@src/types'
import FeaturedProductCard from './FeaturedProductCard'
import ManufacturersSlider from './ManufacturersSlider'
import { AccentButton, WhiteButton, colors, starOrbs } from '@src/theme/sharedComponents'
import designConfig from '@design-config'

const AUTO_ADVANCE_MS = 4500

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`

const Section = styled.header<{ $stars?: boolean }>`
  background: ${colors.primaryDark};
  width: 100%;
  display: flex;
  flex-direction: column;
  border-bottom: 3px solid ${colors.primary};
  position: relative;
  overflow: hidden;
  ${p => p.$stars && starOrbs}
`

const HeroTop = styled.div<{ $hasLogo: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${p => p.$hasLogo ? 'flex-start' : 'center'};
  padding: 3rem 2rem 2.5rem;
  gap: 4rem;
  text-align: ${p => p.$hasLogo ? 'left' : 'center'};
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 900px) {
    flex-direction: column;
    text-align: center;
    padding: 2rem 1.5rem;
    gap: 1.5rem;
  }
`

const HeroText = styled.div`
  ${css`animation: ${fadeIn} 0.6s ease-out both;`}
  flex: 1;
`

const HeroEyebrow = styled.div`
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${colors.accent};
  margin-bottom: 0.6rem;
`

const HeroTitle = styled.h1`
  font-size: clamp(2rem, 6vw, 4.5rem);
  font-weight: 900;
  line-height: 0.95;
  margin: 0 0 1rem;
  color: white;
  font-family: 'Barlow Condensed', 'Impact', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.02em;
`

const HeroSubtitle = styled.p<{ $centered: boolean }>`
  font-size: clamp(0.9rem, 1.4vw, 1rem);
  color: rgba(255,255,255,0.6);
  line-height: 1.6;
  margin: 0 ${p => p.$centered ? 'auto' : '0'} 1.75rem;
  max-width: 480px;

  @media (max-width: 900px) { margin-left: auto; margin-right: auto; }
`

const HeroButtons = styled.div<{ $centered: boolean }>`
  display: flex;
  gap: 0.75rem;
  justify-content: ${p => p.$centered ? 'center' : 'flex-start'};

  @media (max-width: 900px) { justify-content: center; }
  @media (max-width: 400px) { flex-direction: column; align-items: stretch; }
`

const HeroLogoSlot = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  ${css`animation: ${fadeIn} 0.6s ease-out 0.15s both;`}

  @media (max-width: 900px) { order: -1; }
`

const HeroLogoImg = styled.img`
  max-height: 260px;
  max-width: 420px;
  width: auto;
  object-fit: contain;
  filter: drop-shadow(0 4px 24px rgba(0,0,0,0.4));

  @media (max-width: 600px) { max-height: 130px; max-width: 240px; }
`

const StripBar = styled.div`
  background: rgba(0,0,0,0.2);
  border-top: 1px solid rgba(255,255,255,0.06);
`

const StripHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 1.5rem 0.4rem;
  max-width: 1400px;
  margin: 0 auto;
`

const StripLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const StripTitle = styled.span`
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: rgba(255,255,255,0.4);
`

const StripViewAll = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${colors.accent};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  transition: opacity 0.15s;
  &:hover { opacity: 0.7; }
`

const StripControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  @media (max-width: 575px) { display: none; }
`

const DotsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
`

const Dot = styled.button<{ $active: boolean }>`
  padding: 0;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${p => p.$active ? colors.accent : 'rgba(255,255,255,0.25)'};
  width: ${p => p.$active ? '14px' : '4px'};
  height: 4px;
  border-radius: 0;
  flex-shrink: 0;
  &:hover { background: ${p => p.$active ? colors.accent : 'rgba(255,255,255,0.5)'}; }
`

const NavArrow = styled.button`
  width: 24px;
  height: 24px;
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 0;
  background: transparent;
  color: rgba(255,255,255,0.6);
  font-size: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.15s, color 0.15s;
  &:hover { border-color: ${colors.accent}; color: ${colors.accent}; }
`

const StripOuter = styled.div`
  width: 100%;
  height: 130px;
  flex-shrink: 0;
`

const StripInner = styled.div`
  position: relative;
  width: 100%;
  height: 130px;
  overflow: hidden;
`

const SlideLayer = styled.div<{ $active: boolean }>`
  position: absolute;
  inset: 0;
  opacity: ${p => p.$active ? 1 : 0};
  transition: opacity 0.45s ease;
  pointer-events: ${p => p.$active ? 'all' : 'none'};
`

interface LandingPageHeaderProps {
  onProductClick?: (fp: FeaturedProduct) => void
}

const LandingPageHeader: React.FC<LandingPageHeaderProps> = ({ onProductClick }) => {
  const navigate = useNavigate()
  const { data: featuredResponse } = usePublicFeaturedProducts()
  const { data: siteSettings } = usePublicSettings()
  const categoryImageMap = useCategoryImageMap()

  const allProducts = featuredResponse?.products || []
  const showStrip = siteSettings?.landing_featured_products_carousel_enabled !== false
  const featuredProducts = showStrip ? allProducts.slice(0, 10) : []
  const showSlider = siteSettings?.landing_manufacturers_slider_enabled !== false

  const [currentSlide, setCurrentSlide] = useState(0)
  const [paused, setPaused] = useState(false)
  const slideCount = featuredProducts.length

  const next = useCallback(() => setCurrentSlide(p => (p + 1) % slideCount), [slideCount])
  const prev = useCallback(() => setCurrentSlide(p => (p - 1 + slideCount) % slideCount), [slideCount])

  useEffect(() => {
    if (paused || slideCount <= 1) return
    const t = setInterval(next, AUTO_ADVANCE_MS)
    return () => clearInterval(t)
  }, [paused, slideCount, next])

  const heroLogo = siteSettings?.hero_logo ?? null
  const hasLogo = !!heroLogo

  return (
    <>
      <Section $stars={designConfig.themeDecorations[siteSettings?.theme_id ?? ''] === 'stars'}>
        <HeroTop $hasLogo={hasLogo}>
          <HeroText>
            {siteSettings?.hero_eyebrow && <HeroEyebrow>{siteSettings.hero_eyebrow}</HeroEyebrow>}
            <HeroTitle>{siteSettings?.hero_headline}</HeroTitle>
            <HeroSubtitle $centered={!hasLogo}>{siteSettings?.hero_subheading}</HeroSubtitle>
            <HeroButtons $centered={!hasLogo}>
              <WhiteButton type="button" onClick={() => navigate(siteSettings?.hero_cta_primary_path || '/products')}>
                {siteSettings?.hero_cta_primary_icon && <i className={siteSettings.hero_cta_primary_icon} />}
                {siteSettings?.hero_cta_primary_label || 'Product Catalog'}
              </WhiteButton>
              <AccentButton type="button" onClick={() => navigate(siteSettings?.hero_cta_secondary_path || '/products?filter=featured')}>
                {siteSettings?.hero_cta_secondary_icon && <i className={siteSettings.hero_cta_secondary_icon} />}
                {siteSettings?.hero_cta_secondary_label || 'Featured Products'}
              </AccentButton>
            </HeroButtons>
          </HeroText>
          {heroLogo && (
            <HeroLogoSlot>
              <HeroLogoImg src={heroLogo} alt={siteSettings?.company_name ?? 'Company logo'} />
            </HeroLogoSlot>
          )}
        </HeroTop>

        {showStrip && slideCount > 0 && (
          <StripBar>
            <StripHeader>
              <StripLabel>
                <StripTitle>Featured Products</StripTitle>
                <StripViewAll type="button" onClick={() => navigate('/products?filter=featured')}>
                  View all <i className="bi bi-arrow-right" />
                </StripViewAll>
              </StripLabel>
              {slideCount > 1 && (
                <StripControls>
                  <NavArrow onClick={prev} aria-label="Previous"><i className="bi bi-chevron-left" /></NavArrow>
                  <DotsRow>
                    {featuredProducts.map((_: FeaturedProduct, di: number) => (
                      <Dot key={di} $active={di === currentSlide} onClick={() => setCurrentSlide(di)} aria-label={`Go to slide ${di + 1}`} />
                    ))}
                  </DotsRow>
                  <NavArrow onClick={next} aria-label="Next"><i className="bi bi-chevron-right" /></NavArrow>
                </StripControls>
              )}
            </StripHeader>
            <StripOuter onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
              <StripInner>
                {featuredProducts.map((fp: FeaturedProduct, si: number) => (
                  <SlideLayer key={fp.id ?? si} $active={si === currentSlide}>
                    <FeaturedProductCard fp={fp} onClick={() => onProductClick?.(fp)} categoryImageMap={categoryImageMap} style={{ height: '100%', borderRadius: 0 }} />
                  </SlideLayer>
                ))}
              </StripInner>
            </StripOuter>
          </StripBar>
        )}
      </Section>

      {showSlider && <ManufacturersSlider />}
    </>
  )
}

export default LandingPageHeader
